import React, {useState} from 'react';
import {useDispatch} from 'react-redux';
import {useHistory} from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Input from '@mui/material/Input';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import TextField from '@mui/material/TextField';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Dialog from '@mui/material/Dialog';

import {BluePillButton} from '../../common/components/BluePillButton';
import {register} from '../../redux/slices/auth';
import SecondaryAppBar from '../../lesson-builder/components/SecondayAppBar';
import {isValidEmail} from '../../helpers';
import {PASSWORD_MIN_LENGTH} from '../../constants';

import baseStyles from '../../css/Base.module.css';

export default function Register() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [isPasswordVisible, setPasswordVisible] = useState(false);
  const [errorDialogMessage, setErrorDialogMessage] = useState(null);

  const dispatch = useDispatch();
  const history = useHistory();

  function handleFirstNameChanged(event) {
    setFirstName(event.target.value);
  }
  function handleLastNameChanged(event) {
    setLastName(event.target.value);
  }
  function handleEmailChanged(event) {
    setEmail(event.target.value);
  }
  function handlePasswordChanged(event) {
    setPassword(event.target.value);
  }
  function handleInviteCodeChanged(event) {
    setInviteCode(event.target.value.toUpperCase());
  }

  function isSubmitButtonEnabled() {
    return !!inviteCode && !!firstName && !!lastName && !!email && !!password;
  }

  function validateForm() {
    const errors = {};

    if (!inviteCode) errors.inviteCode = 'This field is required.';

    if (!firstName) errors.firstName = 'This field is required.';
    if (!lastName) errors.lastName = 'This field is required.';

    if (!email) errors.email = 'This field is required.';
    else if (!isValidEmail(email)) errors.email = 'Invalid email address.';

    if (!password) errors.password = 'This field is required.';
    else if (password.length < PASSWORD_MIN_LENGTH)
      errors.password = `Use ${PASSWORD_MIN_LENGTH} or more characters with a mix of uppercase letters, lowercase letters, and numbers.`;

    setFormErrors(errors);

    return Object.keys(errors).length === 0;
  }

  async function handleSubmitForm(event) {
    event.preventDefault();
    const isFormValid = validateForm();

    if (isFormValid) {
      let response;

      try {
        response = await dispatch(register(firstName, lastName, email, password, inviteCode));
        console.log(`register response: ${JSON.stringify(response)}`);
      } catch (error) {
        console.log(`register error: ${JSON.stringify(error)}`);

        const {code, message} = error;

        if (code === 'UserLambdaValidationException' && message.includes('Invalid invite code')) {
          setFormErrors({
            inviteCode: 'Invalid invite code.',
          });
        } else if (code === 'UserLambdaValidationException' && message.includes('Invalid email domain')) {
          setFormErrors({
            email: 'Invalid email domain for provided invite code.',
          });
        } else if (code === 'UserLambdaValidationException' && message.includes('Max users reached')) {
          setErrorDialogMessage('The max user count for this invite code has been reached.');
        } else if (code === 'InvalidParameterException' && message === 'Username should be an email.') {
          setFormErrors({
            email: 'Invalid email address.',
          });
        } else if (
          code === 'InvalidParameterException' &&
          message.includes("Value at 'username' failed to satisfy constraint")
        ) {
          setFormErrors({
            email: 'Invalid email address.',
          });
        } else if (code === 'UsernameExistsException') {
          setFormErrors({
            email: 'An account with that email already exists.',
          });
        } else if (code === 'InvalidPasswordException') {
          setFormErrors({
            password: `Use ${PASSWORD_MIN_LENGTH} or more characters with a mix of uppercase letters, lowercase letters, and numbers.`,
          });
        } else {
          setErrorDialogMessage('An error occurred, try again.');
        }
      }

      if (response?.user && !response?.userConfirmed) {
        history.push(`/confirm?email=${encodeURIComponent(email)}`);
      }
    }
  }

  const handleClickShowPassword = () => {
    setPasswordVisible(!isPasswordVisible);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  function renderErrorDialog() {
    return (
      <Dialog open={!!errorDialogMessage} onClose={() => setErrorDialogMessage(null)}>
        <DialogTitle>Register</DialogTitle>
        <DialogContent>
          <DialogContentText>{errorDialogMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorDialogMessage(null)} color="primary" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <>
      <AppBar position="sticky" className="bg-brandorange">
        <Toolbar variant="dense">
          <Typography variant="h6" className={baseStyles['f-g-1']}>
            Flamingo Learning
          </Typography>
        </Toolbar>
      </AppBar>
      <SecondaryAppBar title="Create your account" />

      {renderErrorDialog()}

      <form className="p-20" onSubmit={handleSubmitForm}>
        <Grid container spacing={3}>
          <Grid item xl={2} lg={4} md={6} sm={8} xs={12}>
            <TextField
              variant="standard"
              margin="dense"
              label="Invite Code"
              onChange={handleInviteCodeChanged}
              value={inviteCode}
              error={!!formErrors.inviteCode}
              helperText={formErrors.inviteCode}
              fullWidth
              autoFocus
            />
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid item xl={1} lg={2} md={3} sm={4} xs={6}>
            <TextField
              variant="standard"
              margin="dense"
              label="First name"
              onChange={handleFirstNameChanged}
              value={firstName}
              fullWidth
            />
          </Grid>
          <Grid item xl={1} lg={2} md={3} sm={4} xs={6}>
            <TextField
              variant="standard"
              margin="dense"
              label="Last name"
              onChange={handleLastNameChanged}
              value={lastName}
              fullWidth
            />
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid item xl={2} lg={4} md={6} sm={8} xs={12}>
            <TextField
              variant="standard"
              margin="dense"
              label="Work email address"
              onChange={handleEmailChanged}
              value={email}
              error={!!formErrors.email}
              helperText={formErrors.email}
              fullWidth
            />
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid item xl={2} lg={4} md={6} sm={8} xs={12}>
            <FormControl fullWidth>
              <InputLabel htmlFor="login-form-password" variant="standard">
                Password
              </InputLabel>
              <Input
                id="login-form-password"
                className={baseStyles['m-t-10']}
                value={password}
                onChange={handlePasswordChanged}
                type={isPasswordVisible ? 'text' : 'password'}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton onClick={handleClickShowPassword} onMouseDown={handleMouseDownPassword} size="large">
                      {isPasswordVisible ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                }
                error={!!formErrors.password}
              />
              {!!formErrors.password ? (
                <FormHelperText error>{formErrors.password}</FormHelperText>
              ) : (
                <FormHelperText variant="standard">{`Use ${PASSWORD_MIN_LENGTH} or more characters with a mix of uppercase letters, lowercase letters, and numbers.`}</FormHelperText>
              )}
            </FormControl>
          </Grid>
        </Grid>

        <Grid container spacing={3} className="m-t-20">
          <Grid item xl={2} lg={4} md={6} sm={8} xs={12} className={baseStyles['text-align-end']}>
            <Button className="m-r-20" onClick={() => history.push('/login')}>
              Cancel
            </Button>
            <BluePillButton type="submit" variant="outlined" disabled={!isSubmitButtonEnabled()}>
              SUBMIT
            </BluePillButton>
          </Grid>
        </Grid>
      </form>
    </>
  );
}
